/**
 * Cart page — the frontend for `docs/architecture/payments/09_CART_DOMAIN.md`.
 * Multi-item companion to `CheckoutPage.tsx`'s single-item "Buy Now" flow;
 * neither page computes a price itself — every amount shown comes from the
 * Cart/Checkout API responses, never calculated client-side.
 *
 * Cart's own response never embeds Collection display metadata (backend
 * deliberately doesn't duplicate Catalog data — 09_CART_DOMAIN.md §1) — this
 * page joins `collectionCode` against `useCollections()`'s own listing
 * purely for display (name/image), the same "reuse, don't duplicate"
 * Catalog convention `ProductDetailPage.tsx` already follows.
 */
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { formatPrice } from '@/src/infrastructure/lib/currency';
import { useCart } from '@/src/modules/cart/useCart';
import { useCartCheckout } from '@/src/modules/cart/useCartCheckout';
import { useCollections } from '@/src/modules/catalog/useCollections';
import { MAX_CART_ITEM_QUANTITY, MAX_UNITS_PER_CHECKOUT } from '@/src/modules/cart/constants';

function readableCode(code: string): string {
  return code
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

export function CartPage() {
  const { cart, loading, mutating, error, reload, updateQuantity, removeItem, clear } = useCart();
  const checkout = useCartCheckout();
  const navigate = useNavigate();

  // A checkout failure before any Purchase was created can mean the cart
  // itself moved (converted/expired/modified elsewhere — CartStateChangedError)
  // — caught in code review: without this, the item list stayed stale,
  // sitting above the error with a Checkout button that would just fail the
  // same way again. Re-fetching reflects reality instead.
  useEffect(() => {
    if (checkout.phase === 'error' && checkout.purchases.length === 0) {
      void reload();
    }
  }, [checkout.phase, checkout.purchases.length, reload]);
  // Display-name lookup only. GET /v1/collections caps pageSize at 50 (MAX_PAGE_SIZE) and answers 400
  // INVALID_PAGINATION above that - the old 200 failed on every cart view. If the catalog ever exceeds
  // this, the fallback is the raw code (readableCode), never a broken page.
  const { data: collections } = useCollections({ pageSize: 50 });

  const displayInfo = useMemo(() => {
    const map = new Map<string, { displayName: string; previewImageUrl: string | null }>();
    for (const collection of collections ?? []) {
      map.set(collection.code, {
        displayName: collection.displayName,
        previewImageUrl: collection.previewImageUrl,
      });
    }
    return map;
  }, [collections]);

  const checkoutBusy = checkout.phase === 'checking-out' || checkout.phase === 'creating-orders';

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-16 sm:px-8">
        <div className="h-8 w-40 animate-pulse rounded-lg bg-surface mb-8" />
        <div className="space-y-4">
          <div className="h-20 animate-pulse rounded-2xl bg-surface" />
          <div className="h-20 animate-pulse rounded-2xl bg-surface" />
        </div>
      </div>
    );
  }

  if (checkout.phase === 'ready-for-payment' || checkout.phase === 'creating-orders' || checkout.purchases.length > 0) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8">
        <h1 className="text-2xl font-bold text-on-surface mb-2">
          {checkout.phase === 'ready-for-payment' ? 'Ready for payment' : 'Preparing your orders…'}
        </h1>
        <p className="text-on-surface-variant mb-6">
          {checkout.phase === 'ready-for-payment'
            ? `${checkout.purchases.length || checkout.progress.total} order${(checkout.purchases.length || checkout.progress.total) === 1 ? '' : 's'} waiting for payment.`
            : checkout.purchases.length > 0 && checkout.progress.total === 0
            ? `${checkout.purchases.length} order${checkout.purchases.length === 1 ? '' : 's'} ready.`
            : `${checkout.progress.completed} of ${checkout.progress.total} order${checkout.progress.total === 1 ? '' : 's'} ready.`}
        </p>

        {checkout.error && (
          <div role="alert" className="mb-6 rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-4 text-status-failed text-sm">
            <p className="font-medium">{checkout.error}</p>
            {checkout.phase === 'error' && checkout.purchases.length > 0 && (
              <button
                type="button"
                onClick={() => void checkout.retryOrderCreation()}
                className="mt-2 font-semibold underline"
              >
                Retry
              </button>
            )}
          </div>
        )}

        <ul className="space-y-3">
          {(checkout.purchases.length > 0
            ? checkout.purchases.map((p) => ({
                purchaseId: p.id,
                orderNumber: p.orderNumber,
                collectionCode: p.collectionCode,
                amountPaise: p.amount?.totalPaise ?? 0,
                currency: p.amount?.currency ?? 'INR',
              }))
            : checkout.checkoutDetails.map((d) => ({
                purchaseId: d.purchaseId,
                orderNumber: '',
                collectionCode: '',
                amountPaise: d.checkout.amountPaise,
                currency: d.checkout.currency,
              }))
          ).map((item) => {
            const detail = checkout.checkoutDetails.find((d) => d.purchaseId === item.purchaseId);
            const payment = checkout.payments[item.purchaseId];
            const paymentPhase = payment?.phase ?? 'idle';
            const amountPaise = detail ? detail.checkout.amountPaise : item.amountPaise;
            const currency = detail ? detail.checkout.currency : item.currency;
            const displayTitle = item.collectionCode
              ? (displayInfo.get(item.collectionCode)?.displayName || readableCode(item.collectionCode))
              : null;

            return (
              <li
                key={item.purchaseId}
                className="rounded-2xl bg-primary/5 border border-primary/20 px-6 py-4 flex items-center gap-3 text-sm"
              >
                {paymentPhase === 'paid' ? (
                  <Icons.CheckCircle size={18} className="text-status-published shrink-0" />
                ) : null}
                <span className="text-on-surface-variant flex-1">
                  {displayTitle && (
                    <span className="font-semibold text-on-surface block mb-0.5">
                      {displayTitle} {item.orderNumber ? `(${item.orderNumber})` : ''}
                    </span>
                  )}
                  {paymentPhase === 'paid' ? (
                    <>
                      You were charged{' '}
                      <span className="font-bold text-on-surface">
                        {formatPrice(amountPaise, currency, { exact: true })}
                      </span>{' '}
                      for this item.
                    </>
                  ) : (
                    <>
                      You'll be charged{' '}
                      <span className="font-bold text-on-surface">
                        {formatPrice(amountPaise, currency, { exact: true })}
                      </span>{' '}
                      via Razorpay for this item.
                    </>
                  )}
                  {paymentPhase === 'payment-failed' && payment?.error && (
                    <span className="block text-status-failed font-medium mt-1">{payment.error}</span>
                  )}
                </span>

                {(paymentPhase === 'idle') && (
                  <Button variant="primary" onClick={() => void checkout.payForOrder(item.purchaseId)}>
                    Pay Now
                  </Button>
                )}
                {(paymentPhase === 'awaiting-gateway' || paymentPhase === 'confirming') && (
                  <span className="text-on-surface-variant text-sm font-medium">
                    {paymentPhase === 'awaiting-gateway' ? 'Opening secure payment…' : 'Confirming payment…'}
                  </span>
                )}
                {paymentPhase === 'paid' && (
                  <span className="text-status-published font-semibold">Paid</span>
                )}
                {paymentPhase === 'payment-failed' && (
                  <Button variant="primary" onClick={() => void checkout.payForOrder(item.purchaseId)}>
                    Retry payment
                  </Button>
                )}
                {paymentPhase === 'confirmation-pending' && (
                  <button
                    type="button"
                    onClick={() => void checkout.refreshOrderStatus(item.purchaseId)}
                    className="font-semibold underline text-on-surface-variant"
                  >
                    Check status
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <h1 className="text-2xl font-bold text-on-surface">Your cart is empty</h1>
        <p className="mt-2 text-on-surface-variant">Browse the catalog to add a Collection.</p>
        <Button className="mt-6" variant="primary" onClick={() => navigate('/catalog')}>
          Browse Catalog
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-5 py-16 sm:px-8">
      <h1 className="text-3xl font-bold text-on-surface mb-8">Your Cart</h1>

      {error && (
        <div role="alert" className="mb-4 rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-4 text-status-failed font-medium text-sm">
          {error}
        </div>
      )}

      {/*
        checkout.error covers every checkout failure that ISN'T a per-line
        rejection (checkout.failures below) — CART_TOO_LARGE_FOR_CHECKOUT,
        CART_EMPTY, a concurrent-modification/converted-cart race, or a
        generic failure. Caught in code review: this page used to render
        checkout.error only inside the ready-for-payment branch, so a
        request that failed before any Purchase was created (purchases.length
        stays 0) fell through to this layout with no error displayed at all —
        the Checkout button's spinner just stopped with nothing on screen.
      */}
      {checkout.error && checkout.failures.length === 0 && (
        <div role="alert" className="mb-4 rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-4 text-status-failed font-medium text-sm">
          {checkout.error}
        </div>
      )}

      {checkout.failures.length > 0 && (
        <div role="alert" className="mb-4 rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-4 text-status-failed text-sm">
          <p className="font-semibold">Some items couldn't be checked out — nothing was charged:</p>
          <ul className="mt-1 list-disc pl-5">
            {checkout.failures.map((failure) => (
              <li key={failure.cartItemId}>
                {displayInfo.get(failure.collectionCode)?.displayName ?? readableCode(failure.collectionCode)} — {failure.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        <div className="md:col-span-2 space-y-4">
          {cart.items.map((item) => {
            const info = displayInfo.get(item.collectionCode);
            return (
              <div
                key={item.id}
                className="flex flex-wrap items-center gap-4 rounded-2xl border border-outline-variant/30 bg-surface p-4"
              >
                {info?.previewImageUrl && (
                  <img src={info.previewImageUrl} alt="" className="h-16 w-16 rounded-xl object-cover" />
                )}
                <div className="flex-1 min-w-[10rem]">
                  <p className="font-semibold text-on-surface">
                    {info?.displayName ?? readableCode(item.collectionCode)}
                  </p>
                  <p className="text-sm text-on-surface-variant">
                    {item.unitAmountPaise != null && item.currency
                      ? formatPrice(item.unitAmountPaise, item.currency)
                      : 'Price unavailable'}
                    {item.priceStale && ' · price may have changed'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={mutating || item.quantity <= 1}
                    onClick={() => void updateQuantity(item.id, item.quantity - 1)}
                    aria-label="Decrease quantity"
                    className="h-8 w-8 rounded-full border border-outline-variant/40 disabled:opacity-40"
                  >
                    −
                  </button>
                  <span className="w-6 text-center">{item.quantity}</span>
                  <button
                    type="button"
                    disabled={mutating || item.quantity >= MAX_CART_ITEM_QUANTITY}
                    onClick={() => void updateQuantity(item.id, item.quantity + 1)}
                    aria-label="Increase quantity"
                    className="h-8 w-8 rounded-full border border-outline-variant/40 disabled:opacity-40"
                  >
                    +
                  </button>
                </div>
                <p className="w-24 text-right font-semibold text-on-surface">
                  {item.lineTotalPaise != null && item.currency ? formatPrice(item.lineTotalPaise, item.currency) : '—'}
                </p>
                <button
                  type="button"
                  disabled={mutating}
                  onClick={() => void removeItem(item.id)}
                  aria-label="Remove item"
                  className="text-on-surface-variant hover:text-status-failed"
                >
                  <Icons.Trash size={18} />
                </button>
              </div>
            );
          })}
          <button
            type="button"
            disabled={mutating}
            onClick={() => void clear()}
            className="text-sm font-semibold text-on-surface-variant hover:text-status-failed"
          >
            Clear cart
          </button>
        </div>

        <div className="flex h-fit flex-col gap-4 rounded-2xl border border-outline-variant/30 bg-surface p-6">
          <div className="flex justify-between text-sm">
            <span className="text-on-surface-variant">Subtotal</span>
            <span className="font-semibold text-on-surface">
              {cart.subtotalPaise != null
                ? formatPrice(cart.subtotalPaise, cart.items.find((i) => i.currency)?.currency ?? 'INR')
                : '—'}
            </span>
          </div>
          <p className="text-xs text-on-surface-variant">
            The final price is recalculated at Checkout — this total is only an estimate.
          </p>
          {/*
            Client-side awareness of the backend's MAX_UNITS_PER_CHECKOUT cap
            — caught in code review: without this, a user could fill a cart
            past the cap with no warning, click Checkout, and land on an
            error telling them to remove items they had no idea were too
            many. Purely a UX nicety; the backend remains the enforced limit.
          */}
          {cart.itemCount > MAX_UNITS_PER_CHECKOUT && (
            <p role="alert" className="text-xs font-medium text-status-failed">
              Your cart has {cart.itemCount} items, over the {MAX_UNITS_PER_CHECKOUT}-item checkout limit. Remove some to check out.
            </p>
          )}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            loading={checkoutBusy}
            disabled={mutating || checkoutBusy || cart.itemCount > MAX_UNITS_PER_CHECKOUT}
            onClick={() => void checkout.runCheckout()}
          >
            {checkout.phase === 'checking-out' ? 'Validating cart…' : 'Checkout'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CartPage;
